import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { BellIcon, BuildingIcon, ChevronDownIcon, LogoutIcon, MenuIcon, SearchIcon } from "./icons";
import { ThemeToggle } from "./ui";
import { type ApiUser, getCompany, getOverdueTaskCount } from "../lib/api";

interface Props {
  user: ApiUser;
  loggingOut: boolean;
  onOpenSearch: () => void;
  onToggleSidebar: () => void;
  onLogout: () => void;
}

export function TopBar({ user, loggingOut, onOpenSearch, onToggleSidebar, onLogout }: Props): JSX.Element {
  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-surface px-3 md:px-6">
      <button
        type="button"
        aria-label="メニュー"
        onClick={onToggleSidebar}
        className="flex h-10 w-10 items-center justify-center rounded-md text-text-secondary hover:bg-bg md:hidden"
      >
        <MenuIcon />
      </button>
      <CompanyBadge isAdmin={user.role === "admin"} />
      <SearchBox onOpenSearch={onOpenSearch} />
      <div className="ml-auto flex items-center gap-1.5">
        <NotificationBell />
        <ThemeToggle />
        <UserMenu user={user} loggingOut={loggingOut} onLogout={onLogout} />
      </div>
    </header>
  );
}

function CompanyBadge({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { data: company } = useQuery({
    queryKey: ["company", "profile"],
    queryFn: getCompany,
    staleTime: 300_000,
  });
  const name = company?.display_name || company?.name || "（会社未設定）";
  return (
    <div className="flex max-w-[14rem] items-center gap-2 rounded-md border border-border bg-bg px-3 py-2 text-sm">
      <BuildingIcon className="h-4 w-4 shrink-0 text-text-tertiary" />
      <span className="truncate font-medium text-text-primary">{name}</span>
      {isAdmin && <span className="hidden shrink-0 text-xs text-text-tertiary sm:inline">（管理者モード）</span>}
      <ChevronDownIcon className="h-4 w-4 shrink-0 text-text-tertiary" />
    </div>
  );
}

function SearchBox({ onOpenSearch }: { onOpenSearch: () => void }): JSX.Element {
  return (
    <>
      <button
        type="button"
        onClick={onOpenSearch}
        className="hidden min-h-10 max-w-md flex-1 items-center gap-2 rounded-md border border-border bg-bg px-3 text-sm text-text-tertiary transition hover:bg-surface md:flex"
      >
        <SearchIcon className="h-4 w-4" />
        <span className="flex-1 text-left">案件・顧客・物件を検索</span>
        <kbd className="rounded border border-border-strong bg-surface px-1.5 py-0.5 text-[10px]">Ctrl K</kbd>
      </button>
      <button
        type="button"
        aria-label="検索"
        onClick={onOpenSearch}
        className="flex h-10 w-10 items-center justify-center rounded-md text-text-secondary hover:bg-bg md:hidden"
      >
        <SearchIcon />
      </button>
    </>
  );
}

function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const { data: overdue = 0 } = useQuery({
    queryKey: ["overdue-count"],
    queryFn: getOverdueTaskCount,
    staleTime: 60_000,
  });
  return (
    <button
      type="button"
      aria-label={overdue > 0 ? `通知 ${overdue} 件` : "通知"}
      onClick={() => navigate("/")}
      className="relative flex h-10 w-10 items-center justify-center rounded-md text-text-secondary hover:bg-bg"
    >
      <BellIcon />
      {overdue > 0 && (
        <span className="absolute right-1.5 top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white">
          {overdue > 99 ? "99+" : overdue}
        </span>
      )}
    </button>
  );
}

function UserMenu({
  user,
  loggingOut,
  onLogout,
}: {
  user: ApiUser;
  loggingOut: boolean;
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const initial = user.name.trim().charAt(0) || "U";
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-bg"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
          {initial}
        </span>
        <span className="hidden max-w-28 truncate font-medium text-text-primary sm:inline">{user.name}</span>
        <ChevronDownIcon className="hidden h-4 w-4 text-text-tertiary sm:block" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[12rem] rounded-md border border-border bg-surface py-1 shadow-floating"
        >
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-semibold text-text-primary">{user.name}</p>
            <p className="truncate text-xs text-text-tertiary">{user.email}</p>
          </div>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm text-text-secondary transition hover:bg-bg hover:text-text-primary"
          >
            設定
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            disabled={loggingOut}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-secondary transition hover:bg-bg hover:text-text-primary disabled:opacity-50"
          >
            <LogoutIcon className="h-4 w-4" />
            {loggingOut ? "ログアウト中…" : "ログアウト"}
          </button>
        </div>
      )}
    </div>
  );
}
